import { useApp, useService, useSession } from 'contexts';
import { Auxiliars, Combines } from 'helpers';
import { IUser } from 'interfaces';
import { useEffect, useState } from 'react';
import { MessageType, OptionType, StringOrOptionType } from 'types';
import { Enums, Helpers, Routes } from 'utils';

export const useShare = (id: string, initParticipants: IUser[] = [], type: Enums.EnumShare, onSubmitCallback?: () => void) => {
  const { user } = useSession();
  const { setFeedback, t } = useApp();
  const { emailService, potluckService, userService } = useService();

  const sharableUrl = type === Enums.EnumShare.Invite ? Helpers.generateShareUrl({ id: id }) : Helpers.encodedUrl(Routes.summary, { id });

  const [users, setUsers] = useState<StringOrOptionType[]>([]);
  const [emails, setEmails] = useState<StringOrOptionType[]>([]);
  const [currentParticipants, setCurrentParticipants] = useState<IUser[]>(initParticipants);

  useEffect(() => {
    const fetchData = async () => {
      const response = await userService.asEmailPairValue();
      const filteredUsers = response.filter(
        (item: StringOrOptionType) => !initParticipants.some(subitem => subitem._id === (typeof item === "string" ? item : item.value))
      );
      setEmails(initParticipants.map((item: IUser) => ({ label: item.email, value: item._id })));
      setUsers(filteredUsers);
    };
    fetchData();
  }, []); // make sure to include initParticipants in dependency array

  function handleOnCopy() {

    const message = Combines.interpolate(t.message.share_invitation, { name: user.name, url: sharableUrl });

    navigator.clipboard.writeText(message);

    setFeedback({
      severity: Enums.EnumFeedback.Success,
      message: t.message.feedback.copied_to_clipboard,
    } as MessageType);
  }

  async function sendEmails(options: OptionType[]): Promise<boolean> {

    const responsePotluck = await potluckService.findById(id);

    if (responsePotluck) {

      // send email
      // options.forEach(async ({ label }: OptionType) => {
      //   if (type === Enums.EnumShare.Invite) {
      //     emailService.invitation(label, t.message.subject.invitation, sharableUrl, responsePotluck.created_by.full_name, responsePotluck);
      //   } else {
      //     emailService.results(label, t.message.subject.share_results, sharableUrl, responsePotluck);
      //   }
      // });

      setFeedback({ message: t.message.feedback.email_has_been_sent, severity: Enums.EnumFeedback.Success } as MessageType);
      return true;
    } else {
      setFeedback({ message: t.message.feedback.request_error, severity: Enums.EnumFeedback.Error } as MessageType);
    }

    return false;
  }

  async function addParticipants(user: string[]): Promise<boolean> {
    const response = await potluckService.addParticipants(id, user);
    if (response) {
      setCurrentParticipants(response.participants);
      setFeedback({ message: t.message.feedback.request_update_success, severity: Enums.EnumFeedback.Success } as MessageType);
      return true;
    } else {
      setFeedback({ message: t.message.feedback.request_error, severity: Enums.EnumFeedback.Error } as MessageType);
    }
    return false;
  }

  async function removeParticipants(user: string[]): Promise<boolean> {
    const response = await potluckService.removeParticipants(id, user);
    if (response) {
      setCurrentParticipants(response.participants);
      setFeedback({ message: t.message.feedback.request_update_success, severity: Enums.EnumFeedback.Success } as MessageType);
      return true;
    } else {
      setFeedback({ message: t.message.feedback.request_error, severity: Enums.EnumFeedback.Error } as MessageType);
    }
    return false;
  }

  const handleOnChange = (emails: StringOrOptionType[]) => setEmails(emails);

  const handleOnSubmit = async () => {
    if (emails.length === 0) {
      return;
    }

    await sendEmails(emails as OptionType[]);
    onSubmitCallback?.();
  };

  return { addParticipants, currentParticipants, emails, handleOnChange, handleOnCopy, handleOnSubmit, removeParticipants, sharableUrl, users };
};
